import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/src/renderer/components/ui/Button'
import { Input } from '@/src/renderer/components/ui/Input'
import { PasswordInput } from '@/src/renderer/components/ui/PasswordInput'
import { Link } from '@/src/renderer/components/ui/Link'
import { ChevronLeft } from 'lucide-react'
import { SocialButton, type SocialProvider } from '@/src/renderer/components/ui/SocialButton'
// Dasselbe App-Logo wie auf dem Willkommensbildschirm — die beiden Bildschirme
// sind ein Weg. Hier stand bis 08.09.2026 eine Wolke; sie behauptete über
// „Konto anlegen", das Konto sei die Cloud. Es ist EIN kostenloses Konto,
// HappyCloud ist nur der Ort (docs/concept/setup-konto-und-tarif.md 2, 3.2).
import appIcon from '@/src/renderer/assets/images/happytask-icon-rounded.png'
import { ForgotPasswordForm } from '../../components/ForgotPasswordForm'
import { ResetPasswordForm } from '../../components/ResetPasswordForm'
import { RedirectingToApp } from '../../components/RedirectingToApp'
import { OpenInAppPrompt } from '../../components/OpenInAppPrompt'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import { useLanguage } from '@/src/renderer/contexts/LanguageProvider'
import { navigateToHome } from '@/src/renderer/routes/navigateToHome'
import { getRouteForStep } from '@/src/renderer/lib/types/setupFlow'
import './SetupPage.scss'

/**
 * SetupPage Component
 *
 * Initial onboarding page that handles both user registration and login.
 * Supports multiple authentication methods and intelligently switches between
 * login and registration modes based on user history.
 *
 * @remarks
 * Authentication Methods:
 * - Email + Password (Login mode)
 * - Email + OTP (Registration mode)
 * - Social Login (Google/Apple) via oauth.happytask.app
 * - Skip to Offline Mode (Electron only)
 *
 * Smart Mode Detection:
 * - Shows Login by default if user has logged in before
 * - Shows Registration for first-time users
 *
 * Navigation Flow:
 * - Registration → OTP Verification → Profile Setup → Questionary → Home
 * - Login → Profile Setup (if incomplete) → Home
 * - Skip → Offline Mode → Home
 *
 * @example
 * ```tsx
 * <Route path="/" element={<SetupPage />} />
 * ```
 */
export default function SetupPage(
  { initialView, onBack }: { initialView?: 'login' | 'register'; onBack?: () => void } = {},
) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Reached this page from inside the app via "Mit HappyCloud verbinden"
  // (offline setup already done)? Then offer a way back so the user isn't
  // stranded on the login page if they change their mind.
  const cameFromConnect = localStorage.getItem('offlineSetupCompleted') === 'true'
  const handleBackToApp = () => navigateToHome(navigate)
  const platformAPI = getPlatformAPI()
  const account = useAccountSource()
  const { language } = useLanguage()
  const [showOpenInAppPrompt, setShowOpenInAppPrompt] = useState(false)
  // Greet the returning user by name on the login title. `lastFullName` is a
  // device-level hint written by updateUserProfile / SetupProfilePage that
  // intentionally survives `wipeUserLocalState` on logout. Dexie is checked
  // as a fallback in case localStorage was cleared but the local profile
  // still exists (e.g. mid-flight offline session).
  const [localUserName, setLocalUserName] = useState<string | null>(() => {
    return localStorage.getItem('lastFullName') || null
  })

  useEffect(() => {
    if (localUserName) return
    account.getLocalUserName()
      .then((name) => {
        if (name) setLocalUserName(name)
      })
      .catch((err) => console.error('[SetupPage] Failed to load local user name:', err))
  }, [localUserName, account])

  /**
   * View State: login, register, forgotPassword, resetPassword
   * Smart Default: Shows login mode if user has logged in before
   */
  type AuthView = 'login' | 'register' | 'forgotPassword' | 'resetPassword'
  const [authView, setAuthView] = useState<AuthView>(() => {
    // Check if we're in password recovery mode
    // Set by:
    //  - AuthProvider (PASSWORD_RECOVERY event from Supabase — PKCE flow)
    //  - main.ts / index.html inline script (legacy hash-based flow)
    const isRecoveryMode = localStorage.getItem('passwordRecoveryMode') === 'true'

    // Electron legacy: Hash is set by main.ts, stored in localStorage AND URL hash
    const hasRecoveryHashElectron = localStorage.getItem('passwordRecoveryHash') !== null
    const hasRecoveryTokenInURL = window.location.hash.includes('type=recovery')

    // Web (PKCE): No hash anymore — flag alone is authoritative, because it's
    // only set by the PASSWORD_RECOVERY event which Supabase emits exclusively
    // after a successful recovery-token exchange.
    // Electron legacy: still requires a token source for safety (prevents stale flags).
    const isValidRecovery = platformAPI.platform === 'web'
      ? isRecoveryMode
      : (isRecoveryMode && (hasRecoveryHashElectron || hasRecoveryTokenInURL))

    // Only show resetPassword if BOTH flag is set AND recovery token exists
    // This prevents showing reset screen on fresh app start when token is expired
    if (isValidRecovery) {
      return 'resetPassword'
    }

    // Clear stale flag if recovery is invalid (no token/hash found)
    if (isRecoveryMode && !isValidRecovery) {
      localStorage.removeItem('passwordRecoveryMode')
      localStorage.removeItem('passwordRecoveryHash') // Clean up hash too
    }

    // Explicit initial view from the caller — e.g. the WelcomeScreen
    // "Anmelden" CTA opens the login view.
    if (initialView) return initialView

    // Smart Default: Shows login mode if user has logged in before
    const hasLoggedInBefore = localStorage.getItem('hasLoggedInBefore')
    const defaultView = hasLoggedInBefore === 'true' ? 'login' : 'register'
    return defaultView
  })

  // Automatic redirect to Electron app when on web and in resetPassword mode
  // ONLY if user came from Electron app (source=electron parameter in URL)
  useEffect(() => {
    if (authView === 'resetPassword' && platformAPI.platform === 'web') {
      // Check URL parameter to determine if this came from Electron or Web
      const urlParams = new URLSearchParams(window.location.search)
      const source = urlParams.get('source')

      // Only trigger deep link if source=electron (user clicked "Passwort vergessen" in Electron app)
      if (source !== 'electron') {
        // User came from web browser "Passwort vergessen" → Stay in browser
        return
      }

      // User came from Electron app → Trigger deep link to open Electron app
      const recoveryHash = sessionStorage.getItem('passwordRecoveryHash') || ''

      // Convert hash to query parameters (Windows strips hash from protocol URLs)
      let deepLink = 'happytask://password-reset/'
      if (recoveryHash) {
        // Remove leading # and convert to query params
        const hashWithoutSymbol = recoveryHash.startsWith('#') ? recoveryHash.substring(1) : recoveryHash
        deepLink = `happytask://password-reset/?${hashWithoutSymbol}`

        // Clean up sessionStorage
        sessionStorage.removeItem('passwordRecoveryHash')
      }

      // Small delay to allow RedirectingToApp component to render first
      const redirectTimer = setTimeout(() => {
        window.location.href = deepLink
      }, 500)
      return () => clearTimeout(redirectTimer)
    }
    return undefined
  }, [authView, platformAPI.platform])

  // Backwards compatibility: isLoginMode computed from authView
  const isLoginMode = authView === 'login'

  // Email & OTP States (for Registration)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [emailErrorMessage, setEmailErrorMessage] = useState('')
  const [showCodeInput, setShowCodeInput] = useState(false)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [canResendCode, setCanResendCode] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(60)

  // Password State (for Login)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState(false)

  // Refs for inputs to set focus on errors
  const emailInputRef = useRef<HTMLInputElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const codeInputRef = useRef<HTMLInputElement>(null)

  // Platform-spezifische CSS-Klasse
  const platformClass = platformAPI.platform === 'electron' ? 'setup-page--electron' : 'setup-page--web'

  // Set focus on email input when page loads or mode changes
  useEffect(() => {
    if (emailInputRef.current && !showCodeInput) {
      emailInputRef.current.focus()
    }
  }, [showCodeInput, isLoginMode])

  // Passwort-Wiederherstellung: Der Nutzer kam über den Link aus der Mail → Passwort neu setzen.
  useEffect(() => {
    return account.onPasswordRecovery(() => {
      setAuthView('resetPassword')
    })
  }, [account])

  // Countdown-Timer für OTP Resend
  useEffect(() => {
    if (!showCodeInput) return

    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          setCanResendCode(true)
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [showCodeInput])

  /**
   * Validates email format using regex
   * @param email - Email address to validate
   * @returns true if email is valid, false otherwise
   */
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * Toggles between login and registration mode
   * Resets all form states when switching modes
   */
  const handleLoginModeToggle = () => {
    // Toggle zwischen Login und Registrierung
    setAuthView(authView === 'login' ? 'register' : 'login')
    // Reset all form states
    setEmail('')
    setPassword('')
    setCode('')
    setEmailError(false)
    setPasswordError(false)
    setCodeError(false)
    setShowCodeInput(false)
  }

  /**
   * Handles email + password login flow
   * Validates credentials, authenticates user, and navigates to home
   */
  const handlePasswordLogin = async () => {
    // Validierung
    if (!isValidEmail(email)) {
      setEmailError(true)
      emailInputRef.current?.focus()
      return
    }

    if (!password || password.length < 6) {
      setPasswordError(true)
      passwordInputRef.current?.focus()
      return
    }

    setEmailError(false)
    setPasswordError(false)
    setIsSubmitting(true)

    try {
      // Login mit Email + Passwort
      const signedIn = await account.loginWithPassword(email, password)

      if (signedIn) {
        // HappyCloud-Sync is a paid add-on — do NOT auto-enable it on login.
        // storageMode stays 'offline' (local-only) until the user activates sync.
        // Merke: User hat sich erfolgreich eingeloggt (für Smart Default)
        localStorage.setItem('hasLoggedInBefore', 'true')
        // Merke: Login mit Passwort → direkt zu Home
        localStorage.setItem('loginMethod', 'password')
        // Setze profileSetupCompleted Flag (Login User hat bereits ein vollständiges Profil)
        localStorage.setItem('profileSetupCompleted', 'true')

        // Connecting offline user: setup is already done locally — mark the cloud
        // flow complete and skip straight into the app (no purpose/intent/onboarding).
        if (localStorage.getItem('offlineSetupCompleted') === 'true') {
          await account.updateSetupProgress('completed').catch(() => {})
          localStorage.setItem('onboarding_completed', 'true')
          navigate('/home')
          return
        }

        // Start setup flow tracking (for returning users)
        await account.startSetup()

        // WICHTIG: Prüfe ob User Setup-Fortschritt hat (könnte beim Onboarding abgebrochen haben)
        const progress = await account.getSetupProgress()

        if (progress && !progress.completed && progress.step !== 'login' && progress.step !== 'profile') {
          // User hat Setup-Fortschritt (aber nicht login/profile) → zur letzten Seite
          const targetRoute = getRouteForStep(progress.step)
          navigate(targetRoute)
        } else {
          // Server says setup is complete — rehydrate localStorage flag that
          // ProtectedRoute relies on (wiped by wipeUserLocalState on logout).
          if (progress?.completed) {
            localStorage.setItem('onboarding_completed', 'true')
          }
          navigate('/home')
        }
      } else {
        // Fehler: Ungültige Credentials
        setPasswordError(true)
        passwordInputRef.current?.focus()
      }
    } catch {
      setPasswordError(true)
      passwordInputRef.current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleContinue = async () => {
    // Login-Modus: Email + Passwort Login
    if (isLoginMode) {
      await handlePasswordLogin()
      return
    }

    // Registrierungs-Modus: Wenn Code-Input bereits sichtbar ist → Code verifizieren
    if (showCodeInput) {
      await handleVerifyCode()
      return
    }

    // Email validieren (für OTP-Versand)
    if (!isValidEmail(email)) {
      setEmailError(true)
      emailInputRef.current?.focus()
      return
    }

    setEmailError(false)
    setIsSubmitting(true)

    try {
      // OTP-Code per Email versenden
      const result = await account.sendEmailCode(email, language)

      if (result.success) {
        // Code-Input anzeigen und Countdown starten
        setShowCodeInput(true)
        setCanResendCode(false)
        setResendCountdown(60)
        setEmailError(false)
        setEmailErrorMessage('')
      } else {
        // Fehler anzeigen - prüfe bekannte Supabase-Fehler
        const errorMsg = result.error || ''
        if (errorMsg.includes('you can only request this after')) {
          // Client-seitiges Cooldown zwischen gleichen Anfragen
          setEmailError(false)
          setEmailErrorMessage(t('setup.otpRateLimitError'))
        } else if (errorMsg.includes('email rate limit exceeded')) {
          // Supabase over_email_send_rate_limit — server-seitiges E-Mail-Limit
          setEmailError(false)
          setEmailErrorMessage(t('setup.otpEmailRateLimitError'))
        } else {
          // Anderer Fehler - markiere E-Mail-Feld rot
          setEmailError(true)
          setEmailErrorMessage('')
          emailInputRef.current?.focus()
        }
      }
    } catch {
      setEmailError(true)
      emailInputRef.current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!code || code.length < 6) {
      setCodeError(true)
      codeInputRef.current?.focus()
      return
    }

    setCodeError(false)
    setIsSubmitting(true)

    try {
      // Code verifizieren
      const verified = await account.verifyEmailCode(email, code)

      if (verified) {
        // Erfolg: OTP verifiziert

        // Sprache am Konto absichern (Edge Case: User hat Sprache zwischen
        // Code-Versand und Code-Eingabe gewechselt)
        await account.syncLanguage(language)

        // Merke: User hat sich erfolgreich eingeloggt (für Smart Default)
        localStorage.setItem('hasLoggedInBefore', 'true')

        // Set loginMethod flag FIRST before any navigation or session refresh
        localStorage.setItem('loginMethod', 'otp')

        // Session neu laden, damit die Konto-Daten aktuell sind
        await account.refreshSession()

        // HappyCloud-Sync is a paid add-on — do NOT auto-enable it after OTP.
        // storageMode stays 'offline' (local-only) until the user activates sync.

        // Start setup flow tracking (for new users)
        // This resets setup_flow_step to 'login' for fresh start
        await account.startSetup()

        // IMPORTANT: Do NOT navigate here!
        // Let SetupFlowAutoResume handle ALL navigation
      } else {
        // Fehler: Ungültiger Code
        setCodeError(true)
        codeInputRef.current?.focus()
      }
    } catch {
      setCodeError(true)
      codeInputRef.current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    if (!canResendCode) return

    setIsSubmitting(true)
    setCodeError(false)

    try {
      const result = await account.sendEmailCode(email, language)

      if (result.success) {
        // Timer neu starten
        setCanResendCode(false)
        setResendCountdown(60)
      }
    } catch {
      // Silent error handling
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Handles OAuth flow by opening system browser to oauth.happytask.app
   * Works for both Electron and Web platforms
   *
   * The `provider` query parameter is what actually selects the provider.
   * The auth page rewrites every path to index.html and reads the provider
   * only from the query, defaulting to Google — without it, the Apple
   * button would silently open a Google login.
   */
  const handleSocialLogin = (provider: SocialProvider) => {
    const platform = platformAPI.platform === 'electron' ? 'electron' : 'web'
    const authUrl = `https://oauth.happytask.app/${provider}?platform=${platform}&provider=${provider}`

    if (platformAPI.platform === 'electron' && window.api) {
      // Electron: Use shell.openExternal
      window.api.openExternal(authUrl)
    } else {
      // Web: Use window.open
      window.open(authUrl, '_blank')
    }
  }

  // Special case: Show full-screen RedirectingToApp on web for password reset from Electron
  if (authView === 'resetPassword' && platformAPI.platform === 'web') {
    const urlParams = new URLSearchParams(window.location.search)
    const source = urlParams.get('source')

    if (source === 'electron') {
      return <RedirectingToApp />
    }
  }

  return (
    <div className={`setup-page ${platformClass}`}>
      {/* Open in App Prompt (Web only, for password reset) */}
      {showOpenInAppPrompt && (
        <OpenInAppPrompt onDismiss={() => setShowOpenInAppPrompt(false)} />
      )}

      {/* Scrollable Container */}
      <div className="setup-page__scrollable-container">
        <div className="setup-page__scrollable-inner">

          {/* Content Area */}
          <div className="setup-page__content-area">
            {/* Left Side - Welcome (hidden for forgotPassword and resetPassword) */}
            {authView !== 'forgotPassword' && authView !== 'resetPassword' && (
              <div className="setup-page__welcome-section">
                <div className="setup-page__welcome-content">
                  <img src={appIcon} alt="" className="setup-page__welcome-icon" />
                  <h1 className="setup-page__welcome-title">
                    {isLoginMode
                      ? (localUserName
                        ? t('setup.welcomeBackName', { name: localUserName })
                        : t('setup.welcomeBack'))
                      : t('setup.registerTitle')}
                  </h1>
                  {!isLoginMode && (
                    <p className="setup-page__welcome-subtitle">
                      {t('setup.subtitleRegister')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Right Side - Form */}
            <div className="setup-page__form-section">
              {/* Forgot Password View */}
              {authView === 'forgotPassword' && (
                <ForgotPasswordForm onBackToLogin={() => setAuthView('login')} />
              )}

              {/* Reset Password View (Both Web and Electron) */}
              {authView === 'resetPassword' && (
                <ResetPasswordForm onBackToLogin={() => {
                  // Clear password recovery flags when user clicks "Back to Login"
                  localStorage.removeItem('passwordRecoveryMode')
                  localStorage.removeItem('passwordRecoveryHash')
                  sessionStorage.removeItem('passwordRecoveryHash')
                  setAuthView('login')
                }} />
              )}

              {/* Login / Register View */}
              {(authView === 'login' || authView === 'register') && (
                <>
              {/* Social Login Buttons */}
              <div className="setup-page__social-buttons">
                <SocialButton provider="google" onClick={() => handleSocialLogin('google')}>{t('setup.continueWithGoogle')}</SocialButton>
                <SocialButton provider="apple" onClick={() => handleSocialLogin('apple')}>{t('setup.continueWithApple')}</SocialButton>
              </div>

              {/* Separator */}
              <div className="setup-page__separator">
                <div className="setup-page__separator-line"></div>
                <span className="setup-page__separator-text">
                  {t('setup.or')}
                </span>
                <div className="setup-page__separator-line"></div>
              </div>

              {/* Email & Password Container */}
              <div className="setup-page__credentials-container">
                {/* Email Input */}
                <Input
                  ref={emailInputRef}
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setEmailError(false)
                    setPasswordError(false) // Auch Password-Error zurücksetzen
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleContinue()
                    }
                  }}
                  placeholder={t('setup.emailPlaceholder')}
                  disabled={showCodeInput && !isLoginMode}
                  error={emailError}
                />
                {emailError && (
                  <p className="setup-page__error-message">{t('setup.emailInvalid')}</p>
                )}
                {emailErrorMessage && (
                  <p className="setup-page__error-message">{emailErrorMessage}</p>
                )}

                {/* Password Input (nur im Login-Modus) */}
                {isLoginMode && (
                  <>
                    <PasswordInput
                      ref={passwordInputRef}
                      name="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value)
                        setPasswordError(false)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleContinue()
                        }
                      }}
                      placeholder={t('setup.passwordPlaceholder')}
                      error={passwordError}
                    />
                    {passwordError && (
                      <p className="setup-page__error-message">{t('setup.passwordInvalid')}</p>
                    )}
                    <div className="setup-page__password-forgot">
                      <Link onClick={() => setAuthView('forgotPassword')}>{t('setup.passwordForgot')}</Link>
                    </div>
                  </>
                )}
              </div>

              {/* Code Input (nur sichtbar nach Email-Versand im Registrierungs-Modus) */}
              {!isLoginMode && showCodeInput && (
                <div className="setup-page__code-section">

                  <Input
                    ref={codeInputRef}
                    type="text"
                    value={code}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '') // Nur Zahlen
                      if (value.length <= 8) {
                        setCode(value)
                        setCodeError(false)
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleContinue()
                      }
                    }}
                    maxLength={8}
                    placeholder={t('setup.codePlaceholder')}
                    autoFocus
                    error={codeError}
                    className="setup-page__code-input"
                  />
                  {codeError && (
                    <p className="setup-page__error-message">{t('setup.codeInvalid')}</p>
                  )}
                  <p className="setup-page__code-sent-message">{t('setup.codeSentMessage')}</p>
                  <button
                    onClick={handleResendCode}
                    disabled={!canResendCode || isSubmitting}
                    className="setup-page__resend-button"
                    type="button"
                  >
                    {canResendCode
                      ? t('setup.resendCode')
                      : t('setup.resendCountdown', { seconds: resendCountdown })}
                  </button>
                </div>
              )}

              {/* Terms Text (nur im Registrierungs-Modus) */}
              {!isLoginMode && (
                <p className="setup-page__terms-text">
                  <span>{t('setup.termsPrefix')} </span>
                  <Link href="https://happytask.app/">{t('setup.termsLink')}</Link>
                  <span> {t('setup.termsMiddle')} </span>
                  <Link href="https://happytask.app/">{t('setup.privacyLink')}</Link>
                  <span>{t('setup.termsSuffix')}</span>
                </p>
              )}
                </>
              )}
            </div>
          </div>

          {/* Bottom Action Bar - Only for login/register views */}
          {(authView === 'login' || authView === 'register') && (
            <div className="setup-page__action-bar">
              <div className="setup-page__action-bar-content">
                {/* Far-left escape. When opened from the WelcomeScreen (onBack
                    set) it returns there; otherwise the in-app "Connect to
                    HappyCloud" flow (cameFromConnect) returns to the app. The
                    two scenarios are mutually exclusive, but onBack wins. */}
                {onBack ? (
                  <Button
                    variant="secondary"
                    className="setup-page__back-to-welcome"
                    icon={<ChevronLeft size={16} aria-hidden />}
                    onClick={onBack}
                  >
                    {t('setup.back')}
                  </Button>
                ) : cameFromConnect ? (
                  <Button
                    variant="secondary"
                    className="setup-page__back-to-app"
                    icon={<ChevronLeft size={16} aria-hidden />}
                    onClick={handleBackToApp}
                  >
                    {t('setup.backToApp')}
                  </Button>
                ) : null}
                <div className="setup-page__sign-in-section">
                  <span className="setup-page__sign-in-text">
                    {isLoginMode ? t('setup.noAccount') : t('setup.hasAccount')}
                  </span>
                  <Link onClick={handleLoginModeToggle}>
                    {isLoginMode ? t('setup.register') : t('setup.signIn')}
                  </Link>
                </div>
                <Button
                  onClick={handleContinue}
                  shortcut={{ kind: 'return' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? isLoginMode
                      ? t('setup.loggingIn')
                      : showCodeInput
                      ? t('setup.verifying')
                      : t('setup.sendingCode')
                    : isLoginMode
                    ? t('setup.login')
                    : t('setup.continue')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
